import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/App";
import { ShieldCheck, CheckCircle2, Circle, Loader2, ChevronDown, ChevronUp } from "lucide-react";

/**
 * 80% Readiness Commitment progress card.
 * Shows live progress against the auto-tracked criteria so users know they
 * are on the guarantee path — a retention + motivation lever.
 */
const GuaranteeEligibilityCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/user/guarantee-eligibility")
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="rounded-2xl border border-border bg-card p-5 flex items-center gap-3">
      <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
      <span className="text-sm text-muted-foreground">Loading your guarantee status…</span>
    </div>
  );
  if (!data || !data.commitment) return null;

  const c = data.commitment;
  const pct = Math.round((c.criteria_met / c.criteria_total) * 100);

  return (
    <div data-testid="guarantee-card" className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/5 to-background p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-primary/15 text-primary flex items-center justify-center">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div>
            <div className="font-semibold">80% Readiness Commitment</div>
            <div className="text-xs text-muted-foreground" data-testid="guarantee-card-progress-label">
              {c.criteria_met}/{c.criteria_total} criteria met · reward: free Pro until your next attempt
            </div>
          </div>
        </div>
        <Link to="/guarantee" className="text-xs text-primary underline hover:no-underline shrink-0" data-testid="guarantee-card-policy-link">
          How it works
        </Link>
      </div>

      {/* Progress bar */}
      <div className="h-2 rounded-full bg-muted overflow-hidden mb-3">
        <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} data-testid="guarantee-progress-bar" />
      </div>

      <button onClick={() => setOpen(v => !v)} data-testid="guarantee-card-toggle"
              className="text-xs text-primary hover:underline flex items-center gap-1">
        {open ? "Hide criteria" : "Show criteria"} {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>

      {open && (
        <ul className="mt-3 space-y-2" data-testid="guarantee-criteria-list">
          {data.criteria.map((cr) => (
            <li key={cr.key} className="flex items-start gap-2 text-xs">
              {cr.met
                ? <CheckCircle2 className="w-4 h-4 text-success shrink-0 mt-0.5" />
                : <Circle className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />}
              <div className="min-w-0">
                <div className="font-medium text-foreground">{cr.label}</div>
                <div className="text-muted-foreground">
                  Your progress: <span className="font-mono">{cr.actual}</span> · Need: <span className="font-mono">{cr.needed}</span>
                </div>
                {!cr.met && cr.hint && <div className="text-muted-foreground italic mt-0.5">{cr.hint}</div>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default GuaranteeEligibilityCard;
